import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

const linkLesson0213 = getLinkForCourseLesson(
  "4eb31243-b6cb-423f-919d-15bba103f7ce",
  "7104d555-6856-483a-9815-af7a75be3ead"
);

const theory = `<p>Sound waves travelling from a source to both ears of a listener get modified on their way by the listener’s head, shoulders, auricles, ear canals, and other anatomical features before reaching the eardrums. Using the concept of signal theory, these modifications can be summarized by a pair of filters (one for each ear) denoted as Head-Related Transfer Function (HRTF) in the frequency domain (as depicted in <a href=# data-img-src=${config.ASSETS_URL}/02/13/theory/figure-1.svg data-img-alt="Figure 1. HRTF and HRIR.">Figure 1</a>), and Head-Related Impulse Responses (HRIR)in the time domain. HRTFs therefore encompass all time, phase, level and spectral alterations inherent to binaural hearing, such as ITDs, IPDs, ILDs and spectral cues (introduced in <a href="${linkLesson0213}" target="_blank">Lesson 2.13</a>).
<figure class=figure><p><img src=${config.ASSETS_URL}/02/13/theory/figure-1.svg alt="Figure 1. HRTF and HRIR."><figcaption class=caption>Figure 1. HRTF and HRIR.</figcaption></figure>
<p>
   In signal processing terms, the sounds reaching the left (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>l</mi>
   </math>
   ) and the right (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   ) ears can be interpreted as the result of a convolution between the unaltered source sound,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , and the HRIR
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   associated with the direction of sound incidence
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mi>φ</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>s</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>φ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>∗</mo>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>φ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   While HRTFs are very distinctive (i.e. individual) for each listener (depending on their anatomy), common patterns in HRTFs observed across different individuals can be attributed to prominent anatomical components of the upper body. For example, HRTFs will typically peak at approximately 3 kHz, meaning that sounds in that frequency range are amplified on their way to the eardrum. This is because our ear canal, with an approximate length of 2.5 cm, resembles a one-ended tube. For wavelengths (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>λ</mi>
   </math>
   ) of 10 cm (corresponding to 3 kHz) this is a quarter wavelength hence causing this amplification [1]. HRTFs also display a dip at around 4 kHz introduced by an anti-resonance at the ear cavity (the place in the ear where earphones are plugged).<br class=newline>
<p>
   Thus, different parts of the head and torso influence the HRTF at different frequencies [1], as illustrated in <a href=# data-img-src=${config.ASSETS_URL}/02/13/theory/figure-3.svg data-img-alt="Figure 2. Influence of different anatomical features on frequency regions of the HRTF [1].">Figure 2</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/13/theory/figure-2.svg alt="Figure 2. Influence of different anatomical features on frequency regions of the HRTF [1]."><figcaption class=caption>Figure 2. Influence of different anatomical features on frequency regions of the HRTF [1].</figcaption></figure>
<p>For sound sources within 1 m from the center of the listener’s head HRTFs are heavily dependant on the distance between the listener and the sound source. These HRTFs are called near-field HRTFs. These HRTFs contain information for distance estimation along with localization. However, for sound sources more than 1 m from the center of the listener’s head, HRTFs become progressively less dependant on distance. These HRTFs are called far-field HRTF [2].
<h3 class=likesectionHead>Direction-dependent and direction-independent changes</h3>
<p>
   As mentioned above, HRTFs are a function of direction
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mi>φ</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and are the result of interactions with different anatomical features along the transmission path of sound. Nonetheless, they can be separated into direction-dependent and direction-independent elements, known as Directional Transfer Function (DTF) and Common Transfer Function (CTF) or Average Transfer Function (ATF) respectively [3]. DTF includes the influences of torso, shoulders, head, and auricles (pinnae), while ATF includes changes introduced by the auricle cavities (cavum conchae) and the ear canal. <a href=# data-img-src=${config.ASSETS_URL}/02/13/theory/figure-3.svg data-img-alt="Figure 3. Direction-dependent and direction-independent components of the HRTF.">Figure 3</a> shows different anatomical features and their influence on the HRTF.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/13/theory/figure-3.svg alt="Figure 3. Direction-dependent and direction-independent components of the HRTF."><figcaption class=caption>Figure 3. Direction-dependent and direction-independent components of the HRTF.</figcaption></figure>
<h3 class=likesectionHead>Measuring HRTFs</h3>
<p>
   In practice, HRTFs are obtained as the ratio between the sound pressure measured at the eardrums (or some point in the ear canal) and the sound pressure measured between both ears when the head is absent. HRTF measurements are generally performed in reflection free environments, such as anechoic chambers, by placing a sound source (e.g. a loudspeaker) in the far-field (region beginning approximately 1 wavelength away from the sound source and continues to infinity[3]) at the desired direction and measuring the transfer function with miniature microphones placed in the ears of a person, as seen in <a href=# data-img-src=${config.ASSETS_URL}/02/13/theory/figure-4.svg data-img-alt="Figure 4. High resolution HRTF measurement arc in an anechoic room of the Institute of Technical Acoustics (ITA) at RWTH Aachen University.">Figure 4</a>.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/13/theory/figure-4.svg alt="Figure 4. High resolution HRTF measurement arc in an anechoic room of the Institute of Technical Acoustics (ITA) at RWTH Aachen University."><figcaption class=caption>Figure 4. High resolution HRTF measurement arc in an anechoic room of the Institute of Technical Acoustics (ITA) at RWTH Aachen University.</figcaption></figure>
<p>The HRTF measurement procedure can be expanded with the purpose of measuring HRTFs for multiple directions in a time-efficient manner. As a result rotating loudspeaker arrays are often setup inside anechoic rooms to cover a greater range of directions [4] and techniques for the simultaneous measurement of multiple transfer functions, based on carefully designed signals and adequate processing techniques [5], are generally used. Other approaches for the acquisition of HRTFs include acoustic simulations (e.g. Finite Element Method (FEM) or Boundary Element Method (BEM) based) using a computer model of an individual’s head [6].
<p>It can be seen that the HRTF measurement process is not very simple and requires sophisticated equipment. Thanks to the research efforts made by different universities and institutions around the world, and the development of standards for the exchange of HRTFs and similar spatial data [7], a wide range of HRTF databases with varying degrees of spatial resolutions, and accompanying information (e.g. 3D head scans) are available for use in all kinds of spatial audio applications [8, 9].
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>ATF
  <dd>Average Transfer Function
  <dt>BEM
  <dd>Boundary Element Method
  <dt>CTF
  <dd>Common Transfer Function
  <dt>DTF
  <dd>Directional Transfer Function
  <dt>FEM
  <dd>Finite Element Method
  <dt>HRIR
  <dd>Head-Related Impulse Responses
  <dt>HRTF
  <dd>Head-Related Transfer Function
  <dt>ILD
  <dd>Interaural Level Difference
  <dt>IPD
  <dd>Iteraural Phase Difference
  <dt>ITD
  <dd>Interaural Time Difference
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-genuit1984 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>Klaus Genuit. “Ein Modell Zur Beschreibung Von Aussenohrübertragungseigenschaften”. Doctoral Thesis. Aachen: RWTH Aachen, Dec. 21, 1984.
   <dt id=X0-yu2018 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>Guangzheng Yu et al. “Near-Field Head-Related Transfer-Function Measurement and Database of Human Subjects”. In: The Journal of the Acoustical Society of America 143.3 (Mar. 2018), EL194–EL198. ISSN: 0001-4966. DOI: <a href="https://doi.org/10/gc9j9v" target="_blank">10/gc9j9v</a>.
   <dt id=X0-langendijk2002 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>Erno H. A. Langendijk and Adelbert W. Bronkhorst. “Contribution of Spectral Cues to Human Sound Localization”. In: The Journal of the Acoustical Society of America 112.4 (Oct. 2002), pp. 1583–1596. ISSN: 0001-4966. DOI: <a href="https://doi.org/10/bb5sft" target="_blank">10/bb5sft</a>.
   <dt id=X0-masiero2012a class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>Bruno Sanches Masiero. Individualized Binaural Technology: Measurement, Equalization and Perceptual Evaluation. Logos Verlag Berlin GmbH, Dec. 20, 2012. 210 pp. ISBN: 978-3-8325-3274-1. Google Books: <a href="http://books.google.com/books?id=WCBvNj6skJgC" target="_blank">WCBvNj6skJgC</a>.
   <dt id=X0-majdak2007 class=thebibliography>[5]
   <dd id=bib-5 class=thebibliography>Piotr Majdak, Peter Balazs, and Bernhard Laback. “Multiple Exponential Sweep Method for Fast Measurement of Head-Related Transfer Functions”. In: Journal of the Audio Engineering Society 55 (July 2007), pp. 623–637.
   <dt id=X0-ziegelwanger2015 class=thebibliography>[6]
   <dd id=bib-6 class=thebibliography>Harald Ziegelwanger, Piotr Majdak, and Wolfgang Kreuzer. “Numerical Calculation of Listener-Specific Head-Related Transfer Functions and Sound Localization: Microphone Model and Mesh Discretization”. In: The Journal of the Acoustical Society of America 138.1 (July 2015), pp. 208–222. ISSN: 0001-4966. DOI: <a href="https://doi.org/10.1121/1.4922518" target="_blank">10.1121/1.4922518</a>.
   <dt id=X0-majdak2013 class=thebibliography>[7]
   <dd id=bib-7 class=thebibliography>Piotr Majdak et al. “Spatially Oriented Format for Acoustics: A Data Exchange Format Representing Head-Related Transfer Functions”. In: May 2013.
   <dt id=X0-brinkmann2017 class=thebibliography>[8]
   <dd id=bib-8 class=thebibliography>Fabian Brinkmann et al. “The FABIAN Head-Related Transfer Function Data Base”. In: (Feb. 9, 2017). DOI: <a href="https://doi.org/10.14279/depositonce-5718.5" target="_blank">10.14279/depositonce-5718.5</a>.
   <dt id=X0-fels2016 class=thebibliography>[9]
   <dd id=bib-9 class=thebibliography>Janina Fels and Ramona Bomhardt. “A High-Resolution Head-Related Transfer Function Dataset and 3D Ear Model Database”. In: The Journal of the Acoustical Society of America 140.4 (Oct. 2016), pp. 3276–3276. ISSN: 0001-4966. DOI: <a href="https://doi.org/10/gm4n6r" target="_blank">10/gm4n6r</a>.
</dl>`;

export default theory;
